import type React from "react";

import type { PortalContainer } from "@remirror/extension-react-component";
import { type NodeViewMethod, NodeViewsExtension } from "remirror";

export type RenderMentionArgs = {
  kind: string;
  id: string;
  label?: string;
  attrs: Record<string, unknown>;
};

export type MentionNodeViewOptions = {
  renderMention?: ((args: RenderMentionArgs) => React.ReactNode) | undefined;
};

export class MentionNodeViewExtension extends NodeViewsExtension {
  get name() {
    return "mentionNodeView" as const;
  }

  private readonly renderMention?: MentionNodeViewOptions["renderMention"];

  constructor(options: MentionNodeViewOptions = {}) {
    super();
    this.renderMention = options.renderMention;
  }

  createNodeViews(): Record<string, NodeViewMethod> {
    return {
      mentionAtom: (props: unknown) => {
        // Remirror passes NodeView props here, but depending on version/bundling
        // it can be either `{ node, ... }` or directly the node-like object.
        // Normalize defensively.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const maybeNode: any = (props as any)?.node ?? props;
        const nodeAttrs = maybeNode?.attrs ?? {};

        const dom = document.createElement("a");
        dom.className = "mention";
        dom.setAttribute("contenteditable", "false");

        const reactMount = document.createElement("span");
        reactMount.style.display = "inline-flex";
        reactMount.style.alignItems = "center";
        dom.appendChild(reactMount);

        const getPortalContainer = (): PortalContainer | null => {
          try {
            const store: any = (this as any).store;
            // Prefer the official store key API (set by ReactComponentExtension.onCreate()).
            if (store?.getStoreKey) {
              return store.getStoreKey("portalContainer") as PortalContainer;
            }
            // Fallback (may throw RMR0003 if not yet registered).
            return store?.portalContainer as PortalContainer;
          } catch {
            return null;
          }
        };

        const atomName = nodeAttrs?.["data-mention-atom-name"];
        const atomId = nodeAttrs?.["data-mention-atom-id"];

        if (atomName) dom.setAttribute("data-mention-atom-name", atomName);
        if (atomId) dom.setAttribute("data-mention-atom-id", atomId);

        const label =
          // MentionAtom usually stores the visible label in attrs.
          nodeAttrs?.label ??
          nodeAttrs?.id ??
          // Fallback if present.
          maybeNode?.textContent ??
          "";

        const render = (n: any) => {
          const attrs = n?.attrs ?? {};
          const kind = attrs?.["data-mention-atom-name"];
          const id = attrs?.["data-mention-atom-id"];

          const renderNode = (reactNode: React.ReactNode) => {
            const portalContainer = getPortalContainer();
            if (!portalContainer) {
              // Portal container not ready yet; fallback to plain text.
              reactMount.textContent =
                typeof reactNode === "string" ? reactNode : label;
              return;
            }
            portalContainer.render({
              container: reactMount,
              Component: () => reactNode,
            });
          };

          if (kind && id && this.renderMention) {
            const args: RenderMentionArgs = {
              kind,
              id,
              label: attrs?.label ?? attrs?.id,
              attrs,
            };
            // When we render a rich user mention (e.g. UserChip) we don't want the underlying
            // anchor to navigate on click. For non-user mentions we keep the link behaviour.
            if (kind === "users") {
              dom.removeAttribute("href");
            } else {
              dom.setAttribute("href", `/${kind}/${id}`);
            }
            renderNode(
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {this.renderMention(args)}
              </span>,
            );
            return;
          }

          // Default: show the plain label (same as old behaviour).
          if (kind && id) {
            dom.setAttribute("href", `/${kind}/${id}`);
          }
          renderNode(
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              {label}
            </span>,
          );
        };
        render(maybeNode);

        return {
          dom,
          update: (updatedNode: any) => {
            if (
              updatedNode?.type &&
              maybeNode?.type &&
              updatedNode.type !== maybeNode.type
            ) {
              return false;
            }
            render(updatedNode);
            return true;
          },
          destroy: () => {
            getPortalContainer()?.remove?.(reactMount);
          },
        };
      },
    };
  }
}

export const createMentionNodeViewExtension = (
  options: MentionNodeViewOptions,
) => new MentionNodeViewExtension(options);
